import React, { useState } from "react";
import { useForm } from "react-hook-form";
import StepBar from "../utils/StepBar";
import { getAuth } from "firebase/auth";
import { assignFreetrialRole } from "../utils/roleManagement";
import { useNavigate } from "react-router-dom";
import { createDocument } from "../../firestore/firestoreService"; // Import the createDocument function
import LoadingCircle from "../utils/LoadingCircle"; // Import LoadingCircle
import { getFunctions, httpsCallable } from "firebase/functions";
import { createMarketingNotification } from "../../services/marketingNotificationService";
import "./ApplicantLandingStepThree.css";

const ApplicantLandingStepThree = ({ applicant, setApplicant, goBack }) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      studyMode: applicant.studyMode || "",
      studyHours: applicant.studyHours || "",
      timeCommitment: applicant.timeCommitment || "",
      paymentMethod: applicant.paymentMethod || "",
      startDate: applicant.startDate || "",
    },
  });

  const navigate = useNavigate();
  const [loading, setLoading] = useState(false); // State to track loading

  const onSubmit = async (data) => {
    const auth = getAuth();
    const user = auth.currentUser;

    if (user) {
      try {
        setLoading(true); // Set loading to true when submission starts

        // Update the applicant state with the form data
        setApplicant((prev) => ({
          ...prev,
          ...data,
          email: user.email, // Set the email from the user's auth profile
          username: user.displayName || "", // Set the username from the user's auth profile, if available
        }));

        // Assign the "freetrial" role to the user
        await assignFreetrialRole(user.uid);
        console.log(`User ${user.uid} role updated to freetrail`);

        // Manually refresh the ID token to get the new custom claim
        await user.getIdToken(true);

        // Prepare the applicant data to be written to Firestore
        const applicantData = {
          ...applicant,
          ...data, // Include the updated form data
          email: user.email, // Use the email in the data
          username: user.displayName || "", // Use the username in the data, if available
          registrationDateTime: new Date().toISOString(),
          isCompleted: true,
          timestamp: { _methodName: "serverTimestamp" },
        };

        // 1) Keep existing write
        const firestorePath = `NSSA-Group/AndUs/Applicants`;
        await createDocument(firestorePath, user.email, applicantData); // Use email as document ID
        console.log("Applicant data saved successfully (legacy path).");

        // 2) Also update the platform Applicants store used by the dashboard service
        try {
          const functions = getFunctions();
          const firestoreOperations = httpsCallable(
            functions,
            "firestoreOperations"
          );
          await firestoreOperations({
            operation: "update",
            company: "TPC_Group",
            collectionBrand: "TechPerformanceCentre",
            systemName: "Applicants",
            uniqueId: user.email,
            data: applicantData,
          });
          console.log(
            "Applicant data saved via firestoreOperations (Applicants table)."
          );
        } catch (fnErr) {
          console.warn(
            "firestoreOperations update failed; dashboard may not see updated Applicants doc:",
            fnErr
          );
        }

        // Notify marketing dashboard of new application
        createMarketingNotification({
          type: "application_submitted",
          title: "New application submitted",
          description: `${applicantData.username || user.email} applied for ${applicantData.program || "a programme"}`,
          metadata: {
            name: applicantData.username || user.displayName || "Applicant",
            email: user.email,
            program: applicantData.program,
          },
        });

        // Navigate to the learner dashboard
        navigate("/learner-dashboard");
      } catch (error) {
        console.error(
          "Error updating user role or saving applicant data:",
          error
        );
      } finally {
        setLoading(false); // Stop loading regardless of success or failure
      }
    }
  };

  return (
    <div className="applicant-step-three-container">
      <div className="header-container">
        <h1 className="main-heading">Almost there...</h1>
      </div>
      <StepBar
        totalSteps={3}
        currentStep={3}
        labels={["Program Selection", "Personal Details", "Preferences"]}
      />

      {loading ? (
        <LoadingCircle /> // Show the loading circle when loading is true
      ) : (
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="dropdown-container">
            <label htmlFor="studyMode">
              Would you like to study online or onsite?
            </label>
            <select
              id="studyMode"
              {...register("studyMode", { required: true })}
              className="dropdown"
            >
              <option value="">Select an option</option>
              <option value="online">Online</option>
              <option value="cape-town-campus">Cape Town Campus</option>
              <option value="pretoria-campus">Pretoria Campus</option>
            </select>
            {errors.studyMode && (
              <span className="error">This field is required</span>
            )}
          </div>

          <div className="dropdown-container">
            <label htmlFor="studyHours">
              How many hours per week can you dedicate to studying?
            </label>
            <select
              id="studyHours"
              {...register("studyHours", { required: true })}
              className="dropdown"
            >
              <option value="">Select an option</option>
              <option value="dedicated">
                I am very dedicated, will put in as many hours as it takes
              </option>
              <option value="10-20">10 - 20 hours</option>
              <option value="5-10">5 - 10 hours</option>
              <option value="less-than-5">Less than 5 hours per week</option>
            </select>
            {errors.studyHours && (
              <span className="error">This field is required</span>
            )}
          </div>

          <div className="dropdown-container">
            <label htmlFor="timeCommitment">Time Commitments</label>
            <select
              id="timeCommitment"
              {...register("timeCommitment", { required: true })}
              className="dropdown"
            >
              <option value="">Select an option</option>
              <option value="after-hours">
                I work full-time Monday to Friday
              </option>
              <option value="weekday-lectures">
                I can attend weekday lectures
              </option>
              <option value="flexible">My time is flexible</option>
            </select>
            {errors.timeCommitment && (
              <span className="error">This field is required</span>
            )}
          </div>

          <div className="dropdown-container">
            <label htmlFor="paymentMethod">
              How will you pay for your course?
            </label>
            <select
              id="paymentMethod"
              {...register("paymentMethod", { required: true })}
              className="dropdown"
            >
              <option value="">Select an option</option>
              <option value="self-funded">I fund myself</option>
              <option value="sponsored">I am sponsored</option>
              <option value="employer-funded">
                My employer is funding my studies
              </option>
              <option value="student-loan">
                I am looking for a student loan
              </option>
              <option value="free-trial">
                I am only looking for the free trial
              </option>
            </select>
            {errors.paymentMethod && (
              <span className="error">This field is required</span>
            )}
          </div>

          <div className="dropdown-container">
            <label htmlFor="startDate">What is your ideal start date?</label>
            <select
              id="startDate"
              {...register("startDate", { required: true })}
              className="dropdown"
            >
              <option value="">Select an option</option>
              <option value="asap">As soon as possible</option>
              <option value="next-2-months">In the next two months</option>
              <option value="next-6-months">In the next six months</option>
              <option value="unsure">I am not sure</option>
            </select>
            {errors.startDate && (
              <span className="error">This field is required</span>
            )}
          </div>

          <div className="button-container">
            <button type="button" onClick={goBack} className="back-button">
              Back
            </button>
            <button type="submit" className="continue-button">
              Continue
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default ApplicantLandingStepThree;
